import { type FormFieldRowRequirement, type FormFieldRowValue } from '@components/form-field-row';
import {
  type FormFieldGroupValue,
  type FormFieldsSectionCustomField,
  type FormFieldsSectionValue,
} from '@components/form-fields-section';
import { FIELD_PHONE, FIELD_TAX_ID_PT } from '@components/forms/utils/validate-import-csv';
import { type Admission } from '@domain/admission';
import { type FormFieldPreset } from '@domain/form-field-presets';
import { type CreateAdmissionRequest } from '@hooks/admissions/type';

const DATA_TYPE_NUMERIC = 'numericlist';
const DATA_TYPE_TEXT = 'text';
const SLUG_FIRST_NAME = 'firstName';
const SLUG_LAST_NAME = 'lastName';

const FILTERED_SLUGS: ReadonlySet<string> = new Set([SLUG_FIRST_NAME, SLUG_LAST_NAME]);

type WireFormField = NonNullable<CreateAdmissionRequest['bookingFormFields']>[number];

interface OfferFormField {
  id: string;
  name: string;
  slug: string;
  dataType: string;
  isRequired: boolean;
  isActive?: boolean;
  min?: number;
  max?: number;
}

const flagsToRequirement = (isActive: boolean | undefined, isRequired: boolean): FormFieldRowRequirement => {
  if (isActive === false) return 'off';
  return isRequired ? 'required' : 'optional';
};

const requirementToFlags = (requirement: FormFieldRowRequirement): { isRequired: boolean; isActive: boolean } => ({
  isRequired: requirement === 'required',
  isActive: requirement !== 'off',
});

const toRowValue = (field: OfferFormField): FormFieldRowValue =>
  field.dataType === DATA_TYPE_NUMERIC
    ? { type: 'number', name: field.name, min: field.min?.toString() ?? '', max: field.max?.toString() ?? '' }
    : { type: 'text', name: field.name };

const numericRange = (min?: number, max?: number): Pick<WireFormField, 'min' | 'max'> => ({
  ...(min !== undefined && { min }),
  ...(max !== undefined && { max }),
});

const customRange = (value: Extract<FormFieldRowValue, { type: 'number' }>): Pick<WireFormField, 'min' | 'max'> => ({
  ...(value.min.trim() !== '' && { min: Number(value.min) }),
  ...(value.max.trim() !== '' && { max: Number(value.max) }),
});

export interface SeedFlags {
  shouldPrecheckTaxIdPt?: boolean;
  isPhoneLockedRequired?: boolean;
  seedAssignedByDefault?: boolean;
}

const seedDefaultRequirements = (
  presets: FormFieldPreset[],
  { shouldPrecheckTaxIdPt = false, isPhoneLockedRequired = false, seedAssignedByDefault = true }: SeedFlags
): Record<string, FormFieldRowRequirement> => {
  if (!seedAssignedByDefault) return {};
  const requirements: Record<string, FormFieldRowRequirement> = {};
  for (const preset of presets) {
    if (FILTERED_SLUGS.has(preset.slug)) continue;
    if (preset.isAssignedByDefault || (isPhoneLockedRequired && preset.slug === FIELD_PHONE)) {
      requirements[preset.slug] = 'required';
    } else if (shouldPrecheckTaxIdPt && preset.slug === FIELD_TAX_ID_PT) {
      requirements[preset.slug] = 'optional';
    }
  }
  return requirements;
};

export const mapHolderGroupFromBackend = (
  offerFields: readonly OfferFormField[] | undefined,
  presets: FormFieldPreset[],
  seedFlags: SeedFlags = {}
): FormFieldGroupValue => {
  if (offerFields === undefined) {
    return { presetRequirements: seedDefaultRequirements(presets, seedFlags), customFields: [] };
  }

  const presetSlugs = new Set(presets.map((preset) => preset.slug));
  const presetRequirements: Record<string, FormFieldRowRequirement> = {};
  const customFields: FormFieldsSectionCustomField[] = [];

  for (const field of offerFields) {
    if (FILTERED_SLUGS.has(field.slug)) continue;
    const requirement = flagsToRequirement(field.isActive, field.isRequired);
    if (presetSlugs.has(field.slug)) {
      presetRequirements[field.slug] = requirement;
    } else {
      customFields.push({ id: field.id, backendId: field.id, value: toRowValue(field), requirement });
    }
  }

  return { presetRequirements, customFields };
};

export const buildFormFieldsValue = ({
  offer,
  presets,
  shouldPrecheckTaxIdPt = false,
  isPhoneLockedRequired = false,
}: {
  offer: Admission | null | undefined;
  presets: FormFieldPreset[];
  shouldPrecheckTaxIdPt?: boolean;
  isPhoneLockedRequired?: boolean;
}): FormFieldsSectionValue => ({
  isNominative: offer?.settings.isNominative ?? false,
  mainHolder: mapHolderGroupFromBackend(offer ? offer.bookingFormFields : undefined, presets, {
    shouldPrecheckTaxIdPt,
    isPhoneLockedRequired,
  }),
  ticketHolder: mapHolderGroupFromBackend(offer ? offer.ticketHolderFormFields : undefined, presets, {
    seedAssignedByDefault: false,
  }),
});

const buildPresetItem = (
  slug: string,
  requirement: FormFieldRowRequirement,
  preset: FormFieldPreset,
  offerId: string | undefined
): WireFormField | null => {
  if (requirement === 'off' && offerId === undefined) return null;
  return {
    ...(offerId !== undefined && { id: offerId }),
    name: preset.name,
    slug: preset.slug,
    dataType: preset.dataType,
    ...requirementToFlags(requirement),
    ...numericRange(preset.min, preset.max),
  };
};

const buildCustomItem = (custom: FormFieldsSectionCustomField): WireFormField | null => {
  if (custom.requirement === 'off' && custom.backendId === undefined) return null;
  const isNumeric = custom.value.type === 'number';
  return {
    ...(custom.backendId !== undefined && { id: custom.backendId }),
    name: custom.value.name,
    slug: null,
    dataType: isNumeric ? DATA_TYPE_NUMERIC : DATA_TYPE_TEXT,
    ...requirementToFlags(custom.requirement),
    ...(custom.value.type === 'number' ? customRange(custom.value) : {}),
  };
};

const buildDeletes = (
  group: FormFieldGroupValue,
  offerFields: readonly OfferFormField[],
  presetSlugs: Set<string>
): WireFormField[] => {
  const keptIds = new Set(group.customFields.map((custom) => custom.backendId).filter(Boolean));
  return offerFields
    .filter((field) => !presetSlugs.has(field.slug) && !FILTERED_SLUGS.has(field.slug) && !keptIds.has(field.id))
    .map((field) => ({
      id: field.id,
      name: field.name,
      slug: field.slug ?? null,
      dataType: field.dataType,
      isRequired: field.isRequired,
      deleted: true,
      ...numericRange(field.min, field.max),
    }));
};

export const mapHolderGroupToBackend = (
  group: FormFieldGroupValue,
  offerFields: readonly OfferFormField[] | undefined,
  presets: FormFieldPreset[]
): WireFormField[] => {
  const presetBySlug = new Map(presets.map((preset) => [preset.slug, preset]));
  const offerIdBySlug = new Map((offerFields ?? []).map((field) => [field.slug, field.id]));

  const presetItems = Object.entries(group.presetRequirements)
    .filter(([slug]) => !FILTERED_SLUGS.has(slug) && presetBySlug.has(slug))
    .map(([slug, requirement]) =>
      buildPresetItem(slug, requirement, presetBySlug.get(slug) as FormFieldPreset, offerIdBySlug.get(slug))
    );

  const customItems = group.customFields.map(buildCustomItem);
  const deletes = buildDeletes(group, offerFields ?? [], new Set(presetBySlug.keys()));

  return [...presetItems, ...customItems, ...deletes].filter((item): item is WireFormField => item !== null);
};
